import React, { useEffect } from 'react';
import CreateEditFieldForm from './CreateEditFieldForm';
import useCreateEditField from './useCreateEditField';
import { EditMode, FunnelField } from '../../../../../../constants/fieldsManagementConsts';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Button, IconButton, Typography } from '@sweep-io/sweep-design';
import { Close as CloseIcon } from '@sweep-io/sweep-design/dist/icons/Close';
import { selectCrmOrg } from '../../../../environments/environmentsReducer';
import { useSelector } from 'react-redux';
import { DialogLoader } from '../../../../../common/dialogs/DialogLoader';

const CreateEditField = ({
  crmOrgId,
  onCreateFieldCb,
  mode,
  initialValues,
  onUpdateFieldCb,
  handleTryCloseDialog,
  isLeadingField,
  closeDialog,
  setIsPristine,
  hideOnCloseButton,
}: {
  crmOrgId: string;
  onCreateFieldCb?: (newField: SweepField) => void;
  onUpdateFieldCb?: (updatedField: SweepField) => void;
  mode: EditMode;
  initialValues?: Partial<FunnelField>;
  handleTryCloseDialog: () => void;
  isLeadingField?: boolean;
  closeDialog: () => void;
  setIsPristine: (isPristine: boolean) => void;
  hideOnCloseButton?: boolean;
}) => {
  const crmOrg = useSelector(selectCrmOrg(crmOrgId));
  const isEditMode = mode === EditMode.EDIT;

  const {
    errors,
    values: updatedValues,
    reset: resetForm,
    handleConfirm,
    setFieldValue,
    isSubmitted,
    dirty,
    isLoading,
  } = useCreateEditField({
    initialValues,
    crmOrgId,
    closeCreateDialog: closeDialog,
    onCreateFieldCb,
    onUpdateFieldCb,
  });

  useEffect(() => {
    resetForm();
    // Needs to be called only upon mount. if adding "resetForm" to the dep array,
    // it is being called also when "ConfirmDestructiveDialog" is opened, causing the form to be reset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsPristine(!dirty);
  }, [dirty, setIsPristine]);

  const crmOrgName = crmOrg?.name || '';

  return (
    <>
      <DialogLoader visible={isLoading} />
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }} component="div">
        <Typography variant="h2">{isEditMode ? 'Edit' : 'Create new'} Field</Typography>
        {!hideOnCloseButton && (
          <IconButton variant="flat" onClick={handleTryCloseDialog}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <CreateEditFieldForm
          mode={mode}
          crmOrgId={crmOrgId}
          setFieldValue={setFieldValue}
          values={updatedValues}
          errors={isSubmitted ? errors : {}}
          isLeadingField={isLeadingField}
        />
      </DialogContent>
      <DialogActions>
        <Button size="medium" disabled={isLoading} onClick={handleTryCloseDialog} variant="system">
          Cancel
        </Button>
        <Button
          size="medium"
          loading={isLoading}
          onClick={() => handleConfirm(mode)}
        >{`Deploy to ${crmOrgName}`}</Button>
      </DialogActions>
    </>
  );
};

export default CreateEditField;
